import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
	Button,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalBody,
	ModalCloseButton,
} from "@chakra-ui/react";
import { FiSearch, FiX } from "react-icons/fi";
import SkeletonBase from "newui/components/Skeleton";
import FallbackImage from "components/FallbackImage/FallbackImage";
import { ProjectDetailCard } from "components/ProjectCard";
import { FullDrawContainer } from "./FullDrawContainer";
import { useCompanyPastProjects } from "../hooks/useCompanyPastProjects";
import {
	useAddProjectToCompare,
	useRemoveProjectFromCompare,
} from "../hooks/useCompareProjectMutations";
import type { PastProject } from "newui/features/Company/types";
import type { CompareProject } from "../types/supplier-search.types";

interface CompareProjectFullDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	companyId: number;
	groupId: number | string;
	compareProjects: (CompareProject | null)[];
	isDirectCompare: boolean;
}

const getSectors = (project: PastProject): { id: number; name: string }[] => {
	if (project.sectors && project.sectors.length > 0) {
		return project.sectors.slice(0, 3);
	}
	return [];
};

const TABLE_HEADERS = [
	{ label: "", width: "w-[80px]" },
	{ label: "Name", width: "flex-1 min-w-[200px]" },
	{ label: "Top 3 Sectors", width: "w-[220px]" },
	{ label: "Construction Value (AUD)", width: "w-[180px]" },
	{ label: "Year Complete", width: "w-[120px]" },
	{ label: "Procurement Model", width: "w-[180px]" },
	{ label: "", width: "w-[100px]" },
];

const ProjectRow: React.FC<{
	project: PastProject;
	isAlreadyCompared: boolean;
	isAdding: boolean;
	onAdd: () => void;
	onRowClick: () => void;
}> = ({ project, isAlreadyCompared, isAdding, onAdd, onRowClick }) => {
	const sectors = getSectors(project);

	return (
		<div
			className="flex cursor-pointer items-center border-b border-gray-100 px-4 py-3 hover:bg-gray-50 even:bg-blue-50"
			onClick={onRowClick}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					onRowClick();
				}
			}}
		>
			<div className="w-[80px] flex-shrink-0 pr-3">
				<FallbackImage
					src={project.images?.[0] ?? ""}
					alt={project.name}
					className="h-[55px] w-full rounded-md object-cover"
				/>
			</div>

			<div className="min-w-[200px] flex-1 pr-3">
				<p className="text-sm font-medium text-gray-900">
					{project.name}
					{project.status === "draft" && (
						<span className="text-gray-400"> - (Draft)</span>
					)}
				</p>
			</div>

			<div className="w-[220px] flex-shrink-0 pr-3">
				{sectors.length > 0 ? (
					<div className="flex flex-col gap-0.5">
						{sectors.map((sector) => (
							<p key={sector.id} className="text-sm text-gray-600">
								{sector.name}
							</p>
						))}
					</div>
				) : (
					<p className="text-sm text-gray-400">—</p>
				)}
			</div>

			<div className="w-[180px] flex-shrink-0 pr-3">
				<p className="text-sm text-gray-600">
					{project.value === 0 || project.value === null
						? "Confidential/NA"
						: `$${project.value_humanized}`}
				</p>
			</div>

			<div className="w-[120px] flex-shrink-0 pr-3">
				<p className="text-sm text-gray-600">
					{project.year_completed || "—"}
				</p>
			</div>

			<div className="w-[180px] flex-shrink-0 pr-3">
				{project.procurement_methods && project.procurement_methods.length > 0 ? (
					<div className="flex flex-col gap-0.5">
						{project.procurement_methods.map((method) => (
							<p key={method.id} className="text-sm text-gray-600">
								{method.name}
							</p>
						))}
					</div>
				) : (
					<p className="text-sm text-gray-400">—</p>
				)}
			</div>

			<div className="w-[100px] flex-shrink-0 text-right">
				<Button
					size="sm"
					colorScheme={isAlreadyCompared ? "gray" : "blue"}
					isDisabled={isAlreadyCompared || isAdding}
					isLoading={isAdding}
					onClick={(e) => {
						e.stopPropagation();
						onAdd();
					}}
				>
					{isAlreadyCompared ? "Added" : "Add"}
				</Button>
			</div>
		</div>
	);
};

export const CompareProjectFullDrawer: React.FC<
	CompareProjectFullDrawerProps
> = ({ isOpen, onClose, companyId, groupId, compareProjects, isDirectCompare }) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedProject, setSelectedProject] = useState<PastProject | null>(null);

	const { data: pastProjects, isLoading } = useCompanyPastProjects(
		isOpen ? companyId : null
	);
	const addMutation = useAddProjectToCompare();
	const removeMutation = useRemoveProjectFromCompare();

	const addProjectIndexes = useSelector(
		(state: any) => state.search.addProjectIndex
	);

	const filteredProjects = useMemo(() => {
		if (!pastProjects) {
			return [];
		}
		if (!searchTerm.trim()) {
			return pastProjects;
		}
		const term = searchTerm.toLowerCase();
		return pastProjects.filter((project) => {
			const name = project.name?.toLowerCase() ?? "";
			const sectorNames = getSectors(project)
				.map((s) => s.name.toLowerCase())
				.join(" ");
			const procurementNames = (project.procurement_methods ?? [])
				.map((m) => m.name.toLowerCase())
				.join(" ");
			const year = project.year_completed?.toLowerCase() ?? "";
			return (
				name.includes(term) ||
				sectorNames.includes(term) ||
				procurementNames.includes(term) ||
				year.includes(term)
			);
		});
	}, [pastProjects, searchTerm]);

	const isAlreadyCompared = (projectId: number): boolean => {
		return compareProjects.some((cp) => cp?.id === projectId);
	};

	const handleAddProject = (project: PastProject) => {
		if (!addProjectIndexes || addProjectIndexes.length === 0) {
			return;
		}

		addMutation.mutateWithRefresh(
			{
				groupId,
				companyId,
				projectId: project.id,
				slot: addProjectIndexes[0],
				project: project as unknown as Record<string, unknown>,
			},
			addProjectIndexes,
			isDirectCompare,
			{
				onSuccess: () => {
					onClose();
				},
			}
		);
	};

	const handleClearSearch = () => {
		setSearchTerm("");
	};

	return (
		<FullDrawContainer
			isOpen={isOpen}
			onClose={onClose}
			title="Select additional projects to add to compare card"
		>
			{/* Search */}
			<div className="mb-4">
				<div className="relative">
					<FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
					<input
						type="text"
						placeholder="Search by name, sector, year or procurement model..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-9 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
					/>
					{searchTerm && (
						<button
							type="button"
							onClick={handleClearSearch}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
						>
							<FiX className="h-4 w-4" />
						</button>
					)}
				</div>
			</div>

			{/* Table header */}
			<div className="flex items-center border-b border-gray-200 px-4 py-2">
				{TABLE_HEADERS.map((header) => (
					<div
						key={header.label || "image"}
						className={`${header.width} flex-shrink-0`}
					>
						{header.label && (
							<p className="text-xs font-medium uppercase tracking-wide text-gray-500">
								{header.label}
							</p>
						)}
					</div>
				))}
			</div>

			{/* Loading */}
			{isLoading && (
				<div className="flex flex-col">
					{[...Array(5)].map((_, i) => (
						<div
							key={i}
							className="flex items-center border-b border-gray-100 px-4 py-3"
						>
							<div className="w-[80px] flex-shrink-0 pr-3">
								<SkeletonBase width="100%" height="55px" />
							</div>
							<div className="min-w-[200px] flex-1 pr-3">
								<SkeletonBase width="80%" height="20px" />
							</div>
							<div className="w-[220px] flex-shrink-0 pr-3">
								<SkeletonBase width="70%" height="20px" />
							</div>
							<div className="w-[180px] flex-shrink-0 pr-3">
								<SkeletonBase width="60%" height="20px" />
							</div>
							<div className="w-[120px] flex-shrink-0 pr-3">
								<SkeletonBase width="50%" height="20px" />
							</div>
							<div className="w-[180px] flex-shrink-0 pr-3">
								<SkeletonBase width="70%" height="20px" />
							</div>
							<div className="w-[100px] flex-shrink-0">
								<SkeletonBase width="70px" height="32px" />
							</div>
						</div>
					))}
				</div>
			)}

			{/* Empty state */}
			{!isLoading && filteredProjects.length === 0 && (
				<div className="flex flex-col items-center justify-center py-12">
					<p className="text-sm text-gray-500">
						{searchTerm
							? "No projects matching your search"
							: "No projects available for this company"}
					</p>
				</div>
			)}

			{/* Project list */}
			{!isLoading && filteredProjects.length > 0 && (
				<div className="flex flex-col">
					{filteredProjects.map((project) => (
						<ProjectRow
							key={project.id}
							project={project}
							isAlreadyCompared={isAlreadyCompared(project.id)}
							isAdding={addMutation.isPending}
							onAdd={() => handleAddProject(project)}
							onRowClick={() => setSelectedProject(project)}
						/>
					))}
				</div>
			)}

			{/* Project detail modal */}
			<Modal
				isOpen={!!selectedProject}
				onClose={() => setSelectedProject(null)}
				size="xl"
				isCentered
			>
				<ModalOverlay />
				<ModalContent maxW="600px" p={0} overflow="hidden">
					<ModalCloseButton zIndex={10} />
					<ModalBody p={0}>
						{selectedProject && (
							<ProjectDetailCard data={selectedProject} />
						)}
					</ModalBody>
				</ModalContent>
			</Modal>
		</FullDrawContainer>
	);
};
